import { InputAdornment } from '@mui/material';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Handler from '../../../../Handler';
import { FormDatePicker } from '../../../../components/form/FormDatePicker';
import { FormInput } from '../../../../components/form/FormInput';
import { reductionFormValidations } from '../form';

export const MonitoringSystem = (props) => {
  const { register, errors, watch, setValue, control } = props;
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const validations = reductionFormValidations(isEn);

  const { reductionBase, reductionBusiness, reductionLeak } = watch();

  useEffect(() => {
    if (
      Number.isFinite(Number(reductionBase)) &&
      Number.isFinite(Number(reductionBusiness)) &&
      Number.isFinite(Number(reductionLeak))
    ) {
      setValue('reduction', Number(reductionBase) - Number(reductionBusiness) - Number(reductionLeak), 'reduction');
    } else {
      setValue('reduction', '');
    }
  }, [reductionBase, reductionBusiness, reductionLeak]);

  return (
    <article className='flex flex-col gap-4 w-[945px] mb-4 py-8'>
      <article className='flex flex-col gap-4 w-[945px] mb-4 pb-8'>
        <header>
          <h2 className='font-semibold mb-4 pb-2'>{t('REDUCTION_MONITORING_INFO')}</h2>
        </header>
        <div className='grid grid-cols-2 w-full gap-4'>
          <FormDatePicker
            label={t('START_DATE')}
            error={errors.startDate}
            required
            control={control}
            name='startDate'
            format='YYYY-MM-DD'
          />
          <FormDatePicker
            label={t('END_DATE')}
            error={errors.endDate}
            required
            control={control}
            minDate={watch('startDate')}
            name='endDate'
            format='YYYY-MM-DD'
          />
        </div>
        <div>
          <FormInput
            label={t('REDUCTION_MONITORING_ORDER')}
            required
            error={errors.monitorDegree}
            value={watch('monitorDegree')}
            disabled
          />
        </div>
      </article>
      <article className='flex flex-col gap-4 w-[945px] mb-4 pb-8'>
        <header>
          <h2 className='font-semibold mb-4 pb-2'>{t('GHG_REDUCTION')}</h2>
        </header>
        <div className='grid grid-cols-2 gap-8'>
          <div className='grid grid-cols-1 gap-4 border border-solid border-gray-300 p-4 rounded-lg'>
            <header>
              <h3>{t('BIZ_PLAN')}</h3>
            </header>
            <Controller
              control={control}
              name='reductionBase'
              disabled
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('BASELINE_EMISSION')}
                    required
                    error={errors.reductionBase}
                    value={displayValue}
                    disabled
                    inputProps={{
                      inputMode: 'numeric',
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name='reductionBusiness'
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('BIZ_EMISSION')}
                    required
                    error={errors.reductionBusiness}
                    value={displayValue}
                    disabled
                    inputProps={{
                      inputMode: 'numeric',
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name='reductionLeak'
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('LEAKAGE')}
                    required
                    error={errors.reductionLeak}
                    value={displayValue}
                    disabled
                    inputProps={{
                      inputMode: 'numeric',
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name='reduction'
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('GHG_REDUCTION_QUANTITY')}
                    required
                    error={errors.reduction}
                    value={displayValue}
                    disabled={true}
                    inputProps={{
                      inputMode: 'numeric',
                      disabled: true,
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
          </div>
          <div className='grid grid-cols-1 gap-4 border border-solid border-gray-300 p-4 rounded-lg'>
            <header>
              <h3>{t('REDUCTION_ACTUAL_RESULT')}</h3>
            </header>
            <Controller
              control={control}
              name='realReductionBase'
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('BASELINE_EMISSION')}
                    required
                    error={errors.realReductionBase}
                    value={displayValue}
                    inputProps={{
                      inputMode: 'numeric',
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name='realReductionBusiness'
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('BIZ_EMISSION')}
                    required
                    error={errors.realReductionBusiness}
                    value={displayValue}
                    inputProps={{
                      inputMode: 'numeric',
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name='realReductionLeak'
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('LEAKAGE')}
                    required
                    error={errors.realReductionLeak}
                    value={displayValue}
                    inputProps={{
                      inputMode: 'numeric',
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name='realReduction'
              render={({ field }) => {
                const displayValue = field.value ? Handler.numberWithCommas(field.value) : '';
                return (
                  <FormInput
                    label={t('GHG_REDUCTION_QUANTITY')}
                    required
                    error={errors.realReduction}
                    value={displayValue}
                    disabled={true}
                    inputProps={{
                      inputMode: 'numeric',
                      disabled: true,
                      ...Handler.createCommaNumberInputHandlers(field.onChange),
                    }}
                    endAdornment={<InputAdornment position='end'>tCO2-eq / year</InputAdornment>}
                  />
                );
              }}
            />
          </div>
        </div>
      </article>
    </article>
  );
};
